import { IconErrorOutline, IconPlus } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { Checkbox } from "@/components/elements/Checkbox";
import { DebounceInput } from "@/components/elements/Input/Input";
import { Select } from "@/components/elements/Select";
import { Tooltip } from "@/components/elements/Tooltip";
import { AppRoutes } from "@/routes/config";
import { black, divider } from "@/theme/colors";
import { TPagination } from "@/types/api";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { ChangeEvent, FC, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useGetIndustryList } from "@/api/services/global/industry";
import { useEnum } from "@/api/services/global/enum";
import { useGetLearnerSchema } from "@/features/learner/hook/useGetLearnerSchema";
import { useMe } from "@/api/services/main/auth";
import { useRSO } from "@/api/services/main/rso";
import { useCompanyInfo } from "@/api/services/main/company";
import { USER_TYPE } from "@/types/enum";
import { useVisible } from "@/hooks/useVisible";
import { BulkSyncLearnerModal } from "@/features/learner/components/learner-list/BulkSyncLearnerModal";
import { ImportLearnerFileModal } from "@/features/learner/components/learner-file-import/ImportLearnerFileModal";
import { LearnerImportMenuButton } from "@/features/learner/components/learner-list/LearnerImportMenuButton";

type TLearnerFilterForm = {
  keyword: string;
  industryId: string;
  planStatus: string;
  isArchive: boolean;
};

type LearnerListTopProps = {
  formValues: TLearnerFilterForm;
  onChangeFormValues: (key: keyof TLearnerFilterForm, val: unknown) => void;
  onSubmit: (vals: TLearnerFilterForm) => void;
  handleClearFilter: () => void;
  pagination?: TPagination;
};

export const LearnerListTop: FC<LearnerListTopProps> = ({ formValues, onChangeFormValues, onSubmit, handleClearFilter, pagination }) => {
  const { isRsoUser } = useGetLearnerSchema();
  const { data: meResult } = useMe();
  const isSupportUser = meResult?.data?.type === USER_TYPE.SUPPORT_USER;
  // RSOアカウントはRSO自身、企業アカウントはログイン中企業のVisa連携状態で判定する。
  const { data: rsoData } = useRSO(isRsoUser);
  const { data: companyData } = useCompanyInfo(isRsoUser ? undefined : meResult?.data?.companyId);
  const isVisaConnected = isRsoUser
    ? !!rsoData?.data?.studyLink?.isConnected
    : !!companyData?.data?.studyLink?.isConnected;
  const bulkSyncModal = useVisible();
  // ファイル (csv/xlsx/xlsm) からの取り込みモーダル。
  const fileImportModal = useVisible();
  const navigate = useNavigate();
  const location = useLocation();
  // 取り込み画面から「戻る」で離脱したときは、取り込み元のモーダルを開き直す (直前の手順へ戻す)。
  // 一度開いたら state を消し、リロードや再描画で再オープンしないようにする。
  useEffect(() => {
    const state = location.state as { openBulkSync?: boolean; openFileImport?: boolean } | null;
    if (!state?.openBulkSync && !state?.openFileImport) return;
    if (state.openBulkSync) bulkSyncModal.open();
    else fileImportModal.open();
    navigate(AppRoutes.learnerList, { replace: true, state: null });
  }, []);
  const { data: planStatusOptions } = useEnum("EPlanStatus");
  const { isLoading: isLoadingIndustryList, industryOptions } = useGetIndustryList();
  const isAllEmpty = useMemo(() => {
    return Boolean(!formValues.keyword && !formValues.industryId && !formValues.planStatus && !formValues.isArchive);
  }, [formValues]);

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChangeFormValues("isArchive", e.target.checked);
  };

  const formatTotal = (total: number = 0) => {
    return total.toLocaleString("en-US");
  };

  return (
    <Stack>
      <Grid container spacing={2}>
        <Grid item xs={12} md>
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="cap12Light">氏名・所属機関で検索</Typography>
              <Tooltip title="以下から検索ができます。" content={isRsoUser ? "氏名、呼び名、所属機関名" : "氏名、呼び名"}>
                <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle", lineHeight: 0 }}>
                  <IconErrorOutline width={15} height={15} color={black[80]} />
                </Box>
              </Tooltip>
            </Stack>
            <DebounceInput
              placeholder="入力してください"
              value={formValues.keyword}
              onChange={(e) => onChangeFormValues("keyword", e.target.value)}
              size="md"
              style={{ width: "100%" }}
            />
            <Checkbox onChange={handleCheckboxChange} label="アーカイブから検索" checked={!!formValues.isArchive} />
          </Stack>
        </Grid>

        <Grid item xs={12} md>
          <Stack spacing={1}>
            <Typography variant="cap12Light">受験分野</Typography>
            <Select
              placeholder="選択してください"
              loading={isLoadingIndustryList}
              value={formValues.industryId}
              onChange={(value) => onChangeFormValues("industryId", value || "")}
              options={industryOptions}
              size="md"
              style={{ width: "100%" }}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} md>
          <Stack spacing={1}>
            <Typography variant="cap12Light">受験ステータス</Typography>
            <Select
              placeholder="選択してください"
              value={formValues.planStatus}
              onChange={(value) => onChangeFormValues("planStatus", value || "")}
              options={planStatusOptions}
              size="md"
              style={{ width: "100%" }}
            />
          </Stack>
        </Grid>

        <Grid item xs="auto">
          <Stack spacing={1}>
            <Typography variant="cap12Light" sx={{ opacity: 0, lineHeight: "20px" }}>
              操作
            </Typography>
            <Grid container>
              <Grid item xs={6} display="flex" alignItems="center" justifyContent="center">
                <Box mr={2}>
                  <Button type="button" variant="text" disabled={isAllEmpty} onClick={handleClearFilter}>
                    <Typography variant="body14Semi">クリア</Typography>
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Button type="button" variant="outline" disabled={isAllEmpty} onClick={() => onSubmit(formValues)}>
                  <Typography variant="body14Semi">検索</Typography>
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </Grid>
      </Grid>

      <Box sx={{ marginBottom: 2, marginTop: 2, color: divider.high, borderTop: "1px solid" }}></Box>

      <Box marginBottom={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body14Semi">
          全 {formatTotal(pagination?.total) ?? 0} 件中 {Math.min((pagination?.page ?? 1) * (pagination?.perPage ?? 10), pagination?.total ?? 0)}{" "}
          件を表示
        </Typography>
        {!isSupportUser && (
          <Box display="flex" gap={2}>
            <LearnerImportMenuButton
              onSelectVisaImport={bulkSyncModal.open}
              onSelectFileImport={fileImportModal.open}
              disabledVisaImport={!isVisaConnected}
            />
            <Button component={Link} to={AppRoutes.learnerCreate} size="md" startIcon={<IconPlus />}>
              <Typography variant="body14Semi">新しい外国人登録</Typography>
            </Button>
          </Box>
        )}
      </Box>

      <BulkSyncLearnerModal rsoModal={bulkSyncModal} />
      {fileImportModal.visible && <ImportLearnerFileModal fileImportModal={fileImportModal} />}
    </Stack>
  );
};
